import React, { useMemo, useState } from 'react';
import Account from '@material-ui/icons/AccountCircleOutlined';
import classNames from 'classnames';
import CustomAvatar from 'components/CustomAvatar/CustomAvatar';
import GridItem from 'components/Grid/GridItem';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import { HeadingSBold, BodyS, BodyM } from 'components/Typography/Typography';
import { translate } from 'components/Translation';
import styles from 'assets/jss/material-dashboard-pro-react/views/numberDetails/detailsStyle';
import { makeStyles } from '@material-ui/core/styles';
import { CorpUserData } from 'utils/api/models/users/corpUserData';
import Add from '@material-ui/icons/Add';
import CustomLink from 'components/CustomLink/CustomLink';
import Badge from 'components/Badge/Badge';
import { capitalize } from 'utils/converters/capitalize';
import CustomButton from 'components/CustomButtons/Button';
import CanPerform from 'components/CanPerform/CanPerform';
import Business from '@material-ui/icons/BusinessOutlined';
import Button from 'components/CustomButtons/Button';
import { NumbersService } from 'services/numbers/numbers';
import { useDispatch } from 'react-redux';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { finalize } from 'rxjs/operators';
import { setToast } from 'redux/modules/toast/actions';
import invalidateCache from 'utils/api/invalidateCache';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import DetachConfirmationDialog from './DetachConfirmationDialog';
import { getLanguage } from 'utils/language/language';

// @ts-ignore
const useStyles = makeStyles(styles);

export interface IUserInformationCardProps {
	loadingCorpUserData: boolean;
	corpUserData: CorpUserData | null;
	openAssignUserDialog: () => void;
	phoneNumber: string;
	fetchCorpUserData: () => void;
}
const UserInformationCard = ({
	loadingCorpUserData,
	corpUserData,
	openAssignUserDialog,
	phoneNumber,
	fetchCorpUserData,
}: IUserInformationCardProps) => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const language = getLanguage();
	const [detachConfirmationOpen, setDetachConfirmationOpen] = useState<boolean>(false);

	const hideDetachConfirmation = () => {
		setDetachConfirmationOpen(false);
	};

	const showDetachConfirmation = () => {
		setDetachConfirmationOpen(true);
	};

	const detachUser = () => {
		if (corpUserData) {
			hideDetachConfirmation();
			dispatch(showLoadingIndicator());
			NumbersService.detachNumbersFromUser(corpUserData.userId, [phoneNumber])
				.pipe(finalize(() => dispatch(hideLoadingIndicator())))
				.subscribe(({ response }) => {
					if (response?.detachNumbersFromUser?.result) {
						dispatch(
							setToast({
								error: false,
								label: translate('numbers.setting.user.detached'),
								actionType: translate('detached'),
							})
						);
						invalidateCache(['getCorpUserDepartmentDataForNumber'], true);
						fetchCorpUserData();
					}
				});
		}
	};

	const user = useMemo(() => {
		if (corpUserData?.userName) {
			return (
				<span>
					<div className={classes.usernameWrapper}>
						<CustomLink color="brand" to={`/dashboard/users/${corpUserData.userId}`} type="bodyMBold">
							{corpUserData.userName}
						</CustomLink>
						<Badge filled hexColor={corpUserData.departmentUiColor}>
							{corpUserData.departmentName}
						</Badge>
					</div>
					<BodyS disableMargin>{capitalize(corpUserData.userType)}</BodyS>
				</span>
			);
		}
		return (
			<div className={classes.userNotAssigned}>
				<BodyS color="secondary-light" disableMargin>
					<>
						{translate('numbers.details.notAssigned')}
						<CanPerform
							action="number-details:assign-user"
							yes={
								<CustomButton color="tertiary-light" onClick={openAssignUserDialog} withIcon>
									<>
										<Add />
										{translate('numbers.details.assign')}
									</>
								</CustomButton>
							}
						/>
					</>
				</BodyS>
			</div>
		);
		// eslint-disable-next-line
	}, [corpUserData, language]);

	return (
		<GridItem xs={12} md={6}>
			<Card className={classes.informationCard}>
				<CardBody className={classes.cardBody}>
					<ConditionalRender
						show={!!corpUserData?.userName}
						onTrue={() => (
							<CanPerform
								action="number-details:detach-user"
								yes={
									<div className={classes.detachButtonWrapper}>
										<Button color="tertiary-light" onClick={showDetachConfirmation}>
											{translate('number.details.detach.user')}
										</Button>
									</div>
								}
							/>
						)}
					/>
					<div className={classes.informationSubheader}>
						<HeadingSBold>{translate('numbers.details.userInformation')}</HeadingSBold>
					</div>
					<div className={classes.dataContainer}>
						<div className={classes.dataIcon}>
							<Account className={classes.grayIcon} />
						</div>
						<div
							className={classNames({
								[classes.dataText]: true,
								[classes.hidden]: loadingCorpUserData,
							})}
						>
							{user}
						</div>
					</div>
					<div className={classes.dataContainer}>
						<div className={classes.dataIcon}>
							<CustomAvatar
								accountId={corpUserData?.accountId}
								alt="Company Logo"
								placeholderIcon={<Business />}
								customClasses={classes.inlineIcon}
							/>
						</div>
						<div className={classes.dataText}>
							<div className={classes.companyName}>
								<BodyM>{corpUserData ? corpUserData.accountName : '-'}</BodyM>
							</div>
						</div>
					</div>
					<DetachConfirmationDialog
						isOpen={detachConfirmationOpen}
						hideDialog={hideDetachConfirmation}
						detach={detachUser}
					/>
				</CardBody>
			</Card>
		</GridItem>
	);
};

export default UserInformationCard;
